// ssn / date / phone_number: inputs with a fixed shape. The mask keeps
// typing honest; validation checks the completed value. Saves on blur.

import React, { useRef, useState } from "react";
import { Controller, useFormContext, Validate } from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { CalendarIcon, LockClosedIcon } from "@heroicons/react/24/outline";

import {
  FieldError,
  FieldHelper,
  FieldLabel,
  InputSpinner,
  inputClass,
} from "./chrome";
import { V2FieldProps } from "./types";
import { checkPhoneNumberIsReal } from "./validatePhone";

interface MaskConfig {
  format: string;
  placeholder: string;
  validate: (digits: string) => string | true;
}

const DATE_PATTERN = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;

const MASKS: Record<string, MaskConfig> = {
  ssn: {
    format: "###-##-####",
    placeholder: "•••-••-••••",
    validate: (value) => {
      const digits = value.replace(/\D/g, "");
      return digits.length === 9 || "Enter all 9 digits";
    },
  },
  date: {
    format: "##/##/####",
    placeholder: "MM/DD/YYYY",
    validate: (value) =>
      DATE_PATTERN.test(value) || "Enter a date as MM/DD/YYYY",
  },
  phone_number: {
    format: "(###) ###-####",
    placeholder: "(555) 555-5555",
    validate: (value) => {
      const digits = value.replace(/\D/g, "");
      return digits.length === 10 || "Enter all 10 digits";
    },
  },
};

export default function MaskedField({ field, onCommit }: V2FieldProps) {
  const { control } = useFormContext();
  const mask = MASKS[field.type];
  // An SSN reads •••-••-1234 while the input is blurred and complete;
  // focusing reveals the real digits for editing. Display only — the form
  // state never changes. `editing` tracks the real input's own focus: the
  // stand-in must never swap in mid-typing, because replacing a focused
  // element kills focus WITHOUT a blur event, so the commit-on-blur would
  // silently never fire (typing the 9th digit used to do exactly that).
  const [editing, setEditing] = useState(false);
  // A value the walk LOADED (prefill, or a previous session's save)
  // already passed the realness check when it was first entered — only a
  // number the user actually typed this session pays the network
  // round trip. Continue re-runs every visible field's validate, so
  // without this a pristine prefilled phone spins on every advance.
  const userEditedRef = useRef(false);

  const validate: Validate<unknown, Record<string, unknown>> = async (
    value,
  ) => {
    if (value === undefined || value === null || value === "") {
      return field.required ? "This is required" : true;
    }
    const shape = mask.validate(String(value));
    if (shape !== true) return shape;
    // Phone numbers get the real-number check on top of the shape check.
    if (field.type === "phone_number" && userEditedRef.current) {
      return checkPhoneNumberIsReal(String(value));
    }
    return true;
  };

  return (
    <Controller
      control={control}
      name={field.ref}
      rules={{ validate }}
      render={({ field: rhf, fieldState: { error, isValidating } }) => {
        const value = typeof rhf.value === "string" ? rhf.value : "";
        const digits = value.replace(/\D/g, "");
        const atRest = field.type === "ssn" && !editing && digits.length === 9;
        return (
          <div>
            <FieldLabel
              text={field.title}
              htmlFor={field.ref}
              optional={!field.required}
            />
            <div className="relative">
              {atRest ? (
                // A visually identical stand-in showing only the last 4.
                // Focusing it (click or tab) swaps the real input back in.
                <input
                  id={field.ref}
                  type="text"
                  readOnly
                  value={`•••-••-${digits.slice(-4)}`}
                  aria-label={`${field.title} — focus to reveal`}
                  className={inputClass(Boolean(error))}
                  onFocus={() => setEditing(true)}
                />
              ) : (
                <PatternFormat
                  id={field.ref}
                  name={rhf.name}
                  getInputRef={rhf.ref}
                  format={mask.format}
                  value={value}
                  autoFocus={editing}
                  onFocus={() => setEditing(true)}
                  placeholder={field.placeholder ?? mask.placeholder}
                  className={`${inputClass(Boolean(error))} ${
                    field.type === "date" || isValidating ? "pr-10" : ""
                  }`}
                  onValueChange={(values, sourceInfo) => {
                    if (sourceInfo.source === "event") {
                      userEditedRef.current = true;
                    }
                    rhf.onChange(values.formattedValue);
                  }}
                  onBlur={() => {
                    rhf.onBlur();
                    setEditing(false);
                    onCommit();
                  }}
                />
              )}
              {field.type === "date" && !isValidating && (
                <CalendarIcon
                  aria-hidden="true"
                  strokeWidth={2}
                  className="pointer-events-none absolute right-[13px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-oat-700"
                />
              )}
              <InputSpinner show={isValidating} />
            </div>
            <FieldHelper text={field.description} />
            {field.sensitive && (
              <p className="mt-1.5 flex items-center gap-1 text-[13px] leading-normal text-oat-700">
                <LockClosedIcon
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0"
                />
                Secured with 256-bit AES encryption
              </p>
            )}
            <FieldError message={error?.message} />
          </div>
        );
      }}
    />
  );
}
